<template>
  <div class="admin-global-settings container py-4">
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb" class="mb-3">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link to="/admin" class="text-decoration-none">
            <font-awesome-icon icon="home" class="me-1" />
            Admin
          </router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">Globale Einstellungen</li>
      </ol>
    </nav>

    <div class="container-fluid p-4" id="main-content">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 class="mb-0">
            <font-awesome-icon icon="cog" class="me-2" />
            Globale Einstellungen
          </h1>
          <p class="text-muted mb-0">
            Systemweite Einstellungen verwalten
          </p>
        </div>
        <BackButton />
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center my-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Einstellungen werden geladen...</span>
        </div>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <font-awesome-icon icon="exclamation-triangle" class="me-2" />
        {{ error }}
      </div>

      <!-- Settings -->
      <div v-else>
        <!-- Maintenance Mode Section -->
        <div class="card shadow-sm mb-4">
          <div class="card-header">
            <h5 class="mb-0">
              <font-awesome-icon icon="tools" class="me-2" />
              Wartungsmodus
            </h5>
          </div>
          <div class="card-body">
            <!-- Current Maintenance Mode -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="form-check form-switch">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="maintenanceMode" 
                    v-model="maintenanceMode"
                    @change="updateMaintenanceMode"
                    :disabled="updating"
                  >
                  <label class="form-check-label" for="maintenanceMode">
                    <strong>Wartungsmodus aktivieren</strong>
                    <div class="small text-muted">
                      Blockiert den Zugriff für normale Benutzer
                    </div>
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="alert alert-warning" v-if="maintenanceMode">
                  <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                  <strong>Warnung:</strong> Der Wartungsmodus ist aktiv!
                </div>
              </div>
            </div>
            
            <div class="mt-3" v-if="maintenanceMode">
              <label for="maintenanceMessage" class="form-label">Wartungsmodus-Nachricht</label>
              <textarea 
                class="form-control" 
                id="maintenanceMessage" 
                v-model="maintenanceMessage"
                @blur="updateMaintenanceMessage"
                :disabled="updating"
                rows="3"
                placeholder="Nachricht, die Benutzern im Wartungsmodus angezeigt wird..."
              ></textarea>
            </div>

            <hr class="my-4">

            <!-- Scheduled Maintenance -->
            <h6 class="mb-3">
              <font-awesome-icon icon="calendar-alt" class="me-2" />
              Geplanter Wartungsmodus
            </h6>
            
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="form-check form-switch">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="scheduledMaintenanceEnabled" 
                    v-model="scheduledMaintenanceEnabled"
                    @change="updateScheduledMaintenanceEnabled"
                    :disabled="updating"
                  >
                  <label class="form-check-label" for="scheduledMaintenanceEnabled">
                    <strong>Geplanten Wartungsmodus aktivieren</strong>
                    <div class="small text-muted">
                      Plant einen zukünftigen Wartungsmodus
                    </div>
                  </label>
                </div>
              </div>
              <div class="col-md-6">
                <div class="alert alert-info" v-if="scheduledMaintenanceEnabled && scheduledMaintenanceStart">
                  <font-awesome-icon icon="info-circle" class="me-2" />
                  <strong>Geplant:</strong> {{ formatScheduledMaintenance() }}
                </div>
              </div>
            </div>

            <div v-if="scheduledMaintenanceEnabled" class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="scheduledMaintenanceStart" class="form-label">Startzeit *</label>
                  <input 
                    type="datetime-local" 
                    class="form-control" 
                    id="scheduledMaintenanceStart" 
                    v-model="scheduledMaintenanceStart"
                    @blur="updateScheduledMaintenanceStart"
                    :disabled="updating"
                    required
                  >
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="scheduledMaintenanceEnd" class="form-label">Endzeit *</label>
                  <input 
                    type="datetime-local" 
                    class="form-control" 
                    id="scheduledMaintenanceEnd" 
                    v-model="scheduledMaintenanceEnd"
                    @blur="updateScheduledMaintenanceEnd"
                    :disabled="updating"
                    required
                  >
                </div>
              </div>
              <div class="col-12">
                <div class="mb-3">
                  <label for="scheduledMaintenanceMessage" class="form-label">Nachricht für geplanten Wartungsmodus</label>
                  <textarea 
                    class="form-control" 
                    id="scheduledMaintenanceMessage" 
                    v-model="scheduledMaintenanceMessage"
                    @blur="updateScheduledMaintenanceMessage"
                    :disabled="updating"
                    rows="3"
                    placeholder="Nachricht, die Benutzern über den geplanten Wartungsmodus angezeigt wird..."
                  ></textarea>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label for="maintenanceNotificationDays" class="form-label">Benachrichtigung (Tage vorher)</label>
                  <input 
                    type="number" 
                    class="form-control" 
                    id="maintenanceNotificationDays" 
                    v-model="maintenanceNotificationDays"
                    @blur="updateMaintenanceNotificationDays"
                    :disabled="updating"
                    min="0"
                    max="30"
                  >
                  <div class="form-text">
                    Anzahl der Tage vor dem Wartungsmodus, wann Benutzer benachrichtigt werden sollen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notification Settings Section -->
        <div class="card shadow-sm mb-4">
          <div class="card-header">
            <h5 class="mb-0">
              <font-awesome-icon icon="bell" class="me-2" />
              E-Mail-Benachrichtigungen
            </h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <label for="notificationEmail" class="form-label">E-Mail für neue Buchungen</label>
                <input 
                  type="email" 
                  class="form-control" 
                  id="notificationEmail" 
                  v-model="notificationEmail"
                  @blur="updateNotificationEmail"
                  :disabled="updating"
                  placeholder="admin@example.com"
                >
                <div class="form-text">
                  E-Mail-Adresse, an die Benachrichtigungen über neue Buchungen gesendet werden
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-check form-switch">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="emailNotificationsEnabled" 
                    v-model="emailNotificationsEnabled"
                    @change="updateEmailNotificationsEnabled"
                    :disabled="updating"
                  >
                  <label class="form-check-label" for="emailNotificationsEnabled">
                    <strong>E-Mail-Benachrichtigungen aktivieren</strong>
                    <div class="small text-muted">
                      Sendet E-Mails bei neuen Buchungen
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- All Settings Section -->
        <div class="card shadow-sm">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <font-awesome-icon icon="list" class="me-2" />
              Alle Einstellungen
            </h5>
            <button 
              class="btn btn-outline-primary btn-sm" 
              @click="showCreateSettingModal"
            >
              <font-awesome-icon icon="plus" class="me-1" />
              Neue Einstellung
            </button>
          </div>
          <div class="card-body">
            <div v-if="settings.length === 0" class="text-center text-muted py-4">
              <font-awesome-icon icon="cog" class="fa-3x mb-3" />
              <p>Keine Einstellungen gefunden</p>
            </div>
            <div v-else class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Schlüssel</th>
                    <th>Wert</th>
                    <th>Typ</th>
                    <th>Öffentlich</th>
                    <th>Beschreibung</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="setting in settings" :key="setting.key">
                    <td>
                      <code>{{ setting.key }}</code>
                    </td>
                    <td>
                      <span v-if="setting.type === 'boolean'" class="badge" :class="setting.value ? 'bg-success' : 'bg-secondary'">
                        {{ setting.value ? 'Ja' : 'Nein' }}
                      </span>
                      <span v-else-if="setting.type === 'json'" class="text-muted small">
                        {{ JSON.stringify(setting.value).substring(0, 50) }}{{ JSON.stringify(setting.value).length > 50 ? '...' : '' }}
                      </span>
                      <span v-else>{{ setting.value }}</span>
                    </td>
                    <td>
                      <span class="badge bg-info">{{ setting.type }}</span>
                    </td>
                    <td>
                      <span class="badge" :class="setting.isPublic ? 'bg-success' : 'bg-secondary'">
                        {{ setting.isPublic ? 'Ja' : 'Nein' }}
                      </span>
                    </td>
                    <td>{{ setting.description || '-' }}</td>
                    <td>
                      <div class="btn-group" role="group">
                        <button 
                          class="btn btn-outline-primary btn-sm" 
                          @click="editSetting(setting)"
                          title="Bearbeiten"
                        >
                          <font-awesome-icon icon="edit" />
                        </button>
                        <button 
                          class="btn btn-outline-danger btn-sm" 
                          @click="deleteSetting(setting)"
                          title="Löschen"
                        >
                          <font-awesome-icon icon="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Setting Modal -->
    <Teleport to="#modalTarget">
      <div v-if="showModal" class="modal-backdrop fade show"></div>
      <div v-if="showModal" class="modal show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-lg" role="document">
          <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <font-awesome-icon icon="cog" class="me-2" />
              {{ editingSetting ? 'Einstellung bearbeiten' : 'Neue Einstellung' }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
              aria-label="Schließen"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveSetting">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="settingKey" class="form-label">Schlüssel *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="settingKey" 
                      v-model="settingForm.key"
                      :disabled="editingSetting"
                      required
                    >
                    <div class="form-text">Eindeutiger Schlüssel für die Einstellung</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label for="settingType" class="form-label">Typ *</label>
                    <select class="form-select" id="settingType" v-model="settingForm.type" required>
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="json">JSON</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div class="mb-3">
                <label for="settingValue" class="form-label">Wert *</label>
                <textarea 
                  v-if="settingForm.type === 'json'"
                  class="form-control" 
                  id="settingValue" 
                  v-model="settingForm.value"
                  rows="4"
                  placeholder='{"key": "value"}'
                ></textarea>
                <input 
                  v-else-if="settingForm.type === 'boolean'"
                  type="checkbox" 
                  class="form-check-input" 
                  id="settingValue" 
                  v-model="settingForm.value"
                >
                <input 
                  v-else
                  :type="settingForm.type === 'number' ? 'number' : 'text'"
                  class="form-control" 
                  id="settingValue" 
                  v-model="settingForm.value"
                  required
                >
              </div>
              
              <div class="mb-3">
                <label for="settingDescription" class="form-label">Beschreibung</label>
                <textarea 
                  class="form-control" 
                  id="settingDescription" 
                  v-model="settingForm.description"
                  rows="2"
                ></textarea>
              </div>
              
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="settingIsPublic" 
                  v-model="settingForm.isPublic"
                >
                <label class="form-check-label" for="settingIsPublic">
                  Öffentlich zugänglich
                </label>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Abbrechen
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="saveSetting"
              :disabled="saving"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ editingSetting ? 'Aktualisieren' : 'Erstellen' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { globalSettingsApi } from '@/api/globalSettings'
import BackButton from '@/components/common/BackButton.vue'
import { showSuccess, showError } from '@/services/notification'
import Swal from 'sweetalert2'

const router = useRouter()

// State
const loading = ref(false)
const error = ref(null)
const updating = ref(false)
const saving = ref(false)
const settings = ref([])
const showModal = ref(false)
const editingSetting = ref(null)

// Form data
const settingForm = ref({
  key: '',
  value: '',
  type: 'string',
  description: '',
  isPublic: false
})

// Specific settings
const maintenanceMode = ref(false)
const maintenanceMessage = ref('')
const notificationEmail = ref('')
const emailNotificationsEnabled = ref(false)

// Scheduled maintenance settings
const scheduledMaintenanceEnabled = ref(false)
const scheduledMaintenanceStart = ref('')
const scheduledMaintenanceEnd = ref('')
const scheduledMaintenanceMessage = ref('')
const maintenanceNotificationDays = ref(3)

// Computed
const isMaintenanceModeActive = computed(() => maintenanceMode.value)

// Methods
const loadSettings = async () => {
  loading.value = true
  error.value = null
  
  try {
    const response = await globalSettingsApi.getAllSettings()
    settings.value = response.settings
    
    // Load specific settings
    const maintenanceModeSetting = settings.value.find(s => s.key === 'maintenance_mode')
    const maintenanceMessageSetting = settings.value.find(s => s.key === 'maintenance_message')
    const notificationEmailSetting = settings.value.find(s => s.key === 'notification_email')
    const emailNotificationsSetting = settings.value.find(s => s.key === 'email_notifications_enabled')
    
    // Scheduled maintenance settings
    const scheduledMaintenanceEnabledSetting = settings.value.find(s => s.key === 'scheduled_maintenance_enabled')
    const scheduledMaintenanceStartSetting = settings.value.find(s => s.key === 'scheduled_maintenance_start')
    const scheduledMaintenanceEndSetting = settings.value.find(s => s.key === 'scheduled_maintenance_end')
    const scheduledMaintenanceMessageSetting = settings.value.find(s => s.key === 'scheduled_maintenance_message')
    const maintenanceNotificationDaysSetting = settings.value.find(s => s.key === 'maintenance_notification_days')
    
    maintenanceMode.value = maintenanceModeSetting?.value || false
    maintenanceMessage.value = maintenanceMessageSetting?.value || ''
    notificationEmail.value = notificationEmailSetting?.value || ''
    emailNotificationsEnabled.value = emailNotificationsSetting?.value || false
    
    // Load scheduled maintenance settings
    scheduledMaintenanceEnabled.value = scheduledMaintenanceEnabledSetting?.value || false
    scheduledMaintenanceStart.value = scheduledMaintenanceStartSetting?.value ? 
      new Date(scheduledMaintenanceStartSetting.value).toLocaleString('sv-SE').slice(0, 16) : ''
    scheduledMaintenanceEnd.value = scheduledMaintenanceEndSetting?.value ? 
      new Date(scheduledMaintenanceEndSetting.value).toLocaleString('sv-SE').slice(0, 16) : ''
    scheduledMaintenanceMessage.value = scheduledMaintenanceMessageSetting?.value || ''
    maintenanceNotificationDays.value = maintenanceNotificationDaysSetting?.value || 3
    
  } catch (err) {
    console.error('Error loading settings:', err)
    error.value = err.response?.data?.message || 'Fehler beim Laden der Einstellungen'
  } finally {
    loading.value = false
  }
}

const updateMaintenanceMode = async () => {
  updating.value = true
  try {
    await globalSettingsApi.updateSetting('maintenance_mode', {
      value: maintenanceMode.value,
      type: 'boolean',
      description: 'Aktiviert oder deaktiviert den Wartungsmodus',
      isPublic: false
    })
    showSuccess('Wartungsmodus wurde aktualisiert')
  } catch (err) {
    console.error('Error updating maintenance mode:', err)
    showError('Fehler beim Aktualisieren des Wartungsmodus')
    // Revert the change
    maintenanceMode.value = !maintenanceMode.value
  } finally {
    updating.value = false
  }
}

const updateMaintenanceMessage = async () => {
  updating.value = true
  try {
    await globalSettingsApi.updateSetting('maintenance_message', {
      value: maintenanceMessage.value,
      type: 'string',
      description: 'Nachricht, die im Wartungsmodus angezeigt wird',
      isPublic: false
    })
    showSuccess('Wartungsmodus-Nachricht wurde aktualisiert')
  } catch (err) {
    console.error('Error updating maintenance message:', err)
    showError('Fehler beim Aktualisieren der Wartungsmodus-Nachricht')
  } finally {
    updating.value = false
  }
}

const updateNotificationEmail = async () => {
  updating.value = true
  try {
    await globalSettingsApi.updateSetting('notification_email', {
      value: notificationEmail.value,
      type: 'string',
      description: 'E-Mail-Adresse für Benachrichtigungen über neue Buchungen',
      isPublic: false
    })
    showSuccess('Benachrichtigungs-E-Mail wurde aktualisiert')
  } catch (err) {
    console.error('Error updating notification email:', err)
    showError('Fehler beim Aktualisieren der Benachrichtigungs-E-Mail')
  } finally {
    updating.value = false
  }
}

const updateEmailNotificationsEnabled = async () => {
  updating.value = true
  try {
    await globalSettingsApi.updateSetting('email_notifications_enabled', {
      value: emailNotificationsEnabled.value,
      type: 'boolean',
      description: 'Aktiviert oder deaktiviert E-Mail-Benachrichtigungen',
      isPublic: false
    })
    showSuccess('E-Mail-Benachrichtigungen wurden aktualisiert')
  } catch (err) {
    console.error('Error updating email notifications:', err)
    showError('Fehler beim Aktualisieren der E-Mail-Benachrichtigungen')
    // Revert the change
    emailNotificationsEnabled.value = !emailNotificationsEnabled.value
  } finally {
    updating.value = false
  }
}

// Scheduled maintenance update functions
const updateScheduledMaintenanceEnabled = async () => {
  updating.value = true
  try {
    await globalSettingsApi.updateSetting('scheduled_maintenance_enabled', {
      value: scheduledMaintenanceEnabled.value,
      type: 'boolean',
      description: 'Aktiviert geplante Wartungsmodi',
      isPublic: false
    })
    showSuccess('Geplanter Wartungsmodus wurde aktualisiert')
  } catch (err) {
    console.error('Error updating scheduled maintenance enabled:', err)
    showError('Fehler beim Aktualisieren des geplanten Wartungsmodus')
    scheduledMaintenanceEnabled.value = !scheduledMaintenanceEnabled.value
  } finally {
    updating.value = false
  }
}

const updateScheduledMaintenanceStart = async () => {
  if (!scheduledMaintenanceStart.value) return
  
  updating.value = true
  try {
    // Convert local datetime to UTC ISO string
    const localDate = new Date(scheduledMaintenanceStart.value)
    const isoString = localDate.toISOString()
    await globalSettingsApi.updateSetting('scheduled_maintenance_start', {
      value: isoString,
      type: 'string',
      description: 'Startzeit des geplanten Wartungsmodus (ISO 8601)',
      isPublic: false
    })
    showSuccess('Startzeit wurde aktualisiert')
  } catch (err) {
    console.error('Error updating scheduled maintenance start:', err)
    showError('Fehler beim Aktualisieren der Startzeit')
  } finally {
    updating.value = false
  }
}

const updateScheduledMaintenanceEnd = async () => {
  if (!scheduledMaintenanceEnd.value) return
  
  updating.value = true
  try {
    // Convert local datetime to UTC ISO string
    const localDate = new Date(scheduledMaintenanceEnd.value)
    const isoString = localDate.toISOString()
    await globalSettingsApi.updateSetting('scheduled_maintenance_end', {
      value: isoString,
      type: 'string',
      description: 'Endzeit des geplanten Wartungsmodus (ISO 8601)',
      isPublic: false
    })
    showSuccess('Endzeit wurde aktualisiert')
  } catch (err) {
    console.error('Error updating scheduled maintenance end:', err)
    showError('Fehler beim Aktualisieren der Endzeit')
  } finally {
    updating.value = false
  }
}

const updateScheduledMaintenanceMessage = async () => {
  updating.value = true
  try {
    await globalSettingsApi.updateSetting('scheduled_maintenance_message', {
      value: scheduledMaintenanceMessage.value,
      type: 'string',
      description: 'Nachricht für den geplanten Wartungsmodus',
      isPublic: true
    })
    showSuccess('Nachricht wurde aktualisiert')
  } catch (err) {
    console.error('Error updating scheduled maintenance message:', err)
    showError('Fehler beim Aktualisieren der Nachricht')
  } finally {
    updating.value = false
  }
}

const updateMaintenanceNotificationDays = async () => {
  updating.value = true
  try {
    await globalSettingsApi.updateSetting('maintenance_notification_days', {
      value: maintenanceNotificationDays.value,
      type: 'number',
      description: 'Anzahl der Tage vor Wartungsmodus für Benachrichtigung',
      isPublic: false
    })
    showSuccess('Benachrichtigungszeitraum wurde aktualisiert')
  } catch (err) {
    console.error('Error updating maintenance notification days:', err)
    showError('Fehler beim Aktualisieren des Benachrichtigungszeitraums')
  } finally {
    updating.value = false
  }
}

const formatScheduledMaintenance = () => {
  if (!scheduledMaintenanceStart.value || !scheduledMaintenanceEnd.value) {
    return 'Keine Zeitangabe'
  }
  
  // Convert local datetime strings to Date objects
  const start = new Date(scheduledMaintenanceStart.value)
  const end = new Date(scheduledMaintenanceEnd.value)
  
  const startStr = start.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  })
  
  const endStr = end.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
  })
  
  return `${startStr} - ${endStr}`
}

const showCreateSettingModal = () => {
  editingSetting.value = null
  settingForm.value = {
    key: '',
    value: '',
    type: 'string',
    description: '',
    isPublic: false
  }
  showModal.value = true
}

const editSetting = (setting) => {
  editingSetting.value = setting
  settingForm.value = {
    key: setting.key,
    value: setting.type === 'json' ? JSON.stringify(setting.value, null, 2) : setting.value,
    type: setting.type,
    description: setting.description || '',
    isPublic: setting.isPublic
  }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingSetting.value = null
  settingForm.value = {
    key: '',
    value: '',
    type: 'string',
    description: '',
    isPublic: false
  }
}

const saveSetting = async () => {
  saving.value = true
  try {
    let value = settingForm.value.value
    
    // Parse value based on type
    if (settingForm.value.type === 'json') {
      try {
        value = JSON.parse(settingForm.value.value)
      } catch (e) {
        showError('Ungültiges JSON-Format')
        return
      }
    } else if (settingForm.value.type === 'number') {
      value = parseFloat(settingForm.value.value)
      if (isNaN(value)) {
        showError('Ungültiger Zahlenwert')
        return
      }
    }
    
    if (editingSetting.value) {
      await globalSettingsApi.updateSetting(settingForm.value.key, {
        value: value,
        type: settingForm.value.type,
        description: settingForm.value.description,
        isPublic: settingForm.value.isPublic
      })
      showSuccess('Einstellung wurde aktualisiert')
    } else {
      await globalSettingsApi.createSetting({
        key: settingForm.value.key,
        value: value,
        type: settingForm.value.type,
        description: settingForm.value.description,
        isPublic: settingForm.value.isPublic
      })
      showSuccess('Einstellung wurde erstellt')
    }
    
    closeModal()
    await loadSettings()
    
  } catch (err) {
    console.error('Error saving setting:', err)
    showError(err.response?.data?.message || 'Fehler beim Speichern der Einstellung')
  } finally {
    saving.value = false
  }
}

const deleteSetting = async (setting) => {
  const result = await Swal.fire({
    title: 'Einstellung löschen?',
    text: `Möchtest du die Einstellung "${setting.key}" wirklich löschen?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Ja, löschen',
    cancelButtonText: 'Abbrechen'
  })

  if (result.isConfirmed) {
    try {
      await globalSettingsApi.deleteSetting(setting.key)
      showSuccess('Einstellung wurde gelöscht')
      await loadSettings()
    } catch (err) {
      console.error('Error deleting setting:', err)
      showError('Fehler beim Löschen der Einstellung')
    }
  }
}

onMounted(() => {
  loadSettings()
})
</script>

<style scoped>
.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.alert-warning {
  border-left: 4px solid #ffc107;
}

.table code {
  font-size: 0.875em;
  background-color: #f8f9fa;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}
</style>
